import { Injectable } from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';

import {AbstractRestService} from '../../shared/types/AbstractRestService';
import {RecipientReadDto} from '../../shared/models/recipient-read-dto.model';
import {HttpService} from '../http.service';
import {ContactSearchOptionsRequest} from '../../shared/models/contactSearchOptionsRequest.model';
import {AppSettingsService} from '../app-settings.service';
import { RecipientCreateDto } from 'src/app/shared/components/unit/unit-payment/models/recipient/recipient-create-dto.model';

@Injectable({
  providedIn: 'root'
})
export class GpRecipientContactsDataService extends AbstractRestService<RecipientReadDto, RecipientReadDto>{
  protected readonly baseEndpoint$ = this.appSettingsService.localEnv ?
    of('https://localhost:44360/api/payments/recipients/') :
    of('payments/recipients/');

  constructor(http: HttpService,
              private appSettingsService: AppSettingsService) {
    super(http);
  }

  protected getSearchOptionsToQuerystring(options: ContactSearchOptionsRequest): string {
    let queryString = super.getSearchOptionsToQuerystring(options);
    const tagQuerystring = (options.tags || [])
      .map(tag => {
        return `&tags=${tag}`;
      })
      .join('');

    if (tagQuerystring) {
      queryString += tagQuerystring;
    }

    if (options.paymentType) {
      queryString += `&paymentType=${options.paymentType}`;
    }

    return queryString;
  }

  /**
   * Creates a new recipient
   * @param recipient Recipient create object
   */
  public createRecipient(recipient: RecipientCreateDto): Observable<RecipientReadDto> {
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.postTyped<RecipientReadDto>(baseEndpoint, recipient)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  /**
   * Gets all recipients tags
   */
  public getTags(): Observable<Map<number, string>> {
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.getTyped<Map<number, string>>(`${baseEndpoint}tags`)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public updateRecipient(recipientId: number, recipient: RecipientCreateDto): Observable<RecipientReadDto> {
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.putTyped<RecipientReadDto>(`${baseEndpoint}${recipientId}`, recipient)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  public deleteMultiple(recipientsIds: number[]) {
    return this.baseEndpoint$.pipe(
      switchMap(baseEndpoint => this.http.postTyped(`${baseEndpoint}delete-multiple`, recipientsIds)),
      catchError(err => {
        return throwError(err);
      })
    );
  }
}
